import { useEffect, useRef } from 'react';

/**
 * Run a callback on an interval — but ONLY while the tab is visible.
 *
 * The browser's Page Visibility API tells us when the user backgrounds
 * the tab. While hidden, the interval is paused (zero CPU, zero
 * network). When the tab becomes visible again the interval resumes,
 * and (by default) fires the callback once immediately so the UI is
 * fresh on return.
 *
 * For data-fetching, prefer TanStack Query's `refetchInterval` paired
 * with the global `refetchIntervalInBackground: false` default — same
 * pause-on-hidden behaviour, integrated with the cache. Reach for this
 * hook only when you need a non-Query interval (animations, manual
 * derived state, custom websocket heartbeats, etc.).
 *
 * @param callback Invoked every `intervalMs` while the tab is visible.
 * @param intervalMs Delay between invocations. Pass `null` to disable.
 * @param opts.runOnVisible Fire once immediately when the tab becomes
 *   visible (and once at mount if visible). Default `true`.
 */
export function useFocusedInterval(
  callback: () => void,
  intervalMs: number | null,
  opts: { runOnVisible?: boolean } = {},
) {
  const { runOnVisible = true } = opts;

  // Stash latest callback so we don't restart the interval just because
  // the closure captured fresh state.
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (intervalMs == null) return;
    if (typeof document === 'undefined') return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const start = (fireImmediately: boolean) => {
      if (timer != null) return;
      if (fireImmediately) callbackRef.current();
      timer = setInterval(() => callbackRef.current(), intervalMs);
    };

    const stop = () => {
      if (timer == null) return;
      clearInterval(timer);
      timer = null;
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        start(runOnVisible);
      } else {
        stop();
      }
    };

    if (document.visibilityState === 'visible') {
      // Don't fire on mount unless `runOnVisible` is on; matches setInterval
      // default-no-immediate-fire semantics for users who want pure cadence.
      start(runOnVisible);
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [intervalMs, runOnVisible]);
}
